import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { ChevronRight, ChevronLeft, Home } from 'lucide-react';
import { safeLocalStorage } from '../utils/theme';

const STORAGE_KEY = 'iceiq.sidebar';
// At or below 900px the sidebar is always the icon rail; below 640px it is hidden.
const WIDE_QUERY = '(min-width: 901px)';

function readPrefersExpanded() {
  try {
    return safeLocalStorage()?.getItem(STORAGE_KEY) !== 'collapsed';
  } catch {
    return true;
  }
}

function storePrefersExpanded(expanded) {
  try {
    safeLocalStorage()?.setItem(STORAGE_KEY, expanded ? 'expanded' : 'collapsed');
  } catch {
    // Storage is blocked: the choice lasts for this page only.
  }
}

function useWide() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(WIDE_QUERY);
    const onChange = () => setWide(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return wide;
}

// Thin league bar: your colour on the active league. Other leagues are neutral
// because their team lists aren't loaded here (spec §5b).
const leagueBar = (color) => ({ width: '3px', height: '22px', borderRadius: '2px', background: color, flexShrink: 0 });

export function Sidebar({ leagues, activeLeagueId, onSelectLeague, isHome = false, statusByLeague = null }) {
  const [prefersExpanded, setPrefersExpanded] = useState(readPrefersExpanded);
  const wide = useWide();
  const expanded = wide && prefersExpanded;
  const toggle = () => {
    const next = !prefersExpanded;
    storePrefersExpanded(next);
    setPrefersExpanded(next);
  };
  const navigate = useNavigate();
  const { user } = useUser();

  const avatarContent = user?.imageUrl ? (
    <div style={{ width: '28px', height: '28px', borderRadius: '6px', overflow: 'hidden', flexShrink: 0 }}>
      <img src={user.imageUrl} alt="account" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </div>
  ) : (
    <div style={{ width: '28px', height: '28px', borderRadius: '6px', flexShrink: 0, background: 'var(--you)', color: 'var(--you-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700 }}>
      {(user?.firstName?.[0] || user?.emailAddresses?.[0]?.emailAddress?.[0] || '?').toUpperCase()}
    </div>
  );

  return (
    <aside
      className="hidden sm:flex ledger-scope"
      style={{
        position: 'sticky', top: 0, height: '100vh',
        width: expanded ? '236px' : '64px',
        transition: 'width 200ms ease',
        flexDirection: 'column',
        flexShrink: 0,
        background: 'var(--ledger-rail)',
        borderRight: '1px solid var(--ledger-line)',
        color: 'var(--ledger-ink)',
        overflow: 'hidden',
        zIndex: 10,
      }}
    >
      {/* Logo + toggle */}
      <div style={{
        height: '56px', display: 'flex', alignItems: 'center',
        padding: expanded ? '0 12px' : '0',
        justifyContent: expanded ? 'space-between' : 'center',
        flexShrink: 0,
        borderBottom: '1px solid var(--ledger-line)',
      }}>
        <span style={{ fontSize: expanded ? '17px' : '13px', fontWeight: 800, letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}>
          {expanded ? 'IceIQ' : 'IQ'}
        </span>
        {wide && (
          <button
            type="button"
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-expanded={expanded}
            onClick={toggle}
            className="ledger-icon-btn"
            style={{ width: '24px', height: '24px' }}
          >
            {expanded
              ? <ChevronLeft aria-hidden="true" style={{ width: '13px', height: '13px' }} />
              : <ChevronRight aria-hidden="true" style={{ width: '13px', height: '13px' }} />}
          </button>
        )}
      </div>

      <div style={{ padding: expanded ? '8px 8px 0' : '8px 0 0', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="Home"
          aria-current={isHome ? 'page' : undefined}
          title={!expanded ? 'Home' : undefined}
          className="ledger-nav-item"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: expanded ? '7px 8px' : '7px 0', justifyContent: expanded ? 'flex-start' : 'center', borderRadius: '6px', width: '100%', cursor: 'pointer' }}
        >
          <Home aria-hidden="true" style={{ width: '15px', height: '15px', flexShrink: 0 }} />
          {expanded && <span style={{ fontSize: '13px', fontWeight: 600 }}>Home</span>}
        </button>
      </div>

      {/* Section label */}
      {expanded && (
        <div style={{ padding: '14px 14px 4px', fontSize: '12px', fontWeight: 600, color: 'var(--ledger-faint)', flexShrink: 0 }}>
          Your leagues
        </div>
      )}

      {/* League list */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: expanded ? '4px 8px' : '4px 0', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {leagues.map(league => {
          const isActive = league.league_id === activeLeagueId;
          const initials = (league.name || '??').slice(0, 2).toUpperCase();
          return (
            <button
              key={league.league_id}
              type="button"
              onClick={() => onSelectLeague(league.league_id)}
              title={!expanded ? league.name : undefined}
              aria-current={isActive ? 'page' : undefined}
              className="ledger-nav-item"
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: expanded ? '7px 8px' : '7px 0',
                justifyContent: expanded ? 'flex-start' : 'center',
                borderRadius: '6px', width: '100%', cursor: 'pointer',
              }}
            >
              <span aria-hidden="true" style={leagueBar(isActive ? 'var(--you)' : 'var(--ledger-line-2)')} />
              {expanded ? (
<span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, textAlign: 'left' }}>
                  <span style={{ fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{league.name}</span>
                  {statusByLeague?.get(String(league.league_id)) && (
                    <span style={{ fontSize: '11.5px', color: 'var(--ledger-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {statusByLeague.get(String(league.league_id))}
                    </span>
                  )}
                </span>
              ) : (
                <span style={{ fontSize: '11px', fontWeight: 700 }}>{initials}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Account */}
      <div style={{ padding: expanded ? '8px 8px 16px' : '8px 0 16px', flexShrink: 0, display: 'flex', justifyContent: expanded ? 'flex-start' : 'center', borderTop: '1px solid var(--ledger-line)' }}>
        <button
          type="button"
          onClick={() => navigate('/settings', { state: { from: window.location.pathname + window.location.search } })}
          aria-label="Settings"
          title={!expanded ? 'Settings' : undefined}
          className="ledger-nav-item"
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: expanded ? '6px 8px' : '6px',
            borderRadius: '6px', width: expanded ? '100%' : 'auto', cursor: 'pointer',
          }}
        >
          {avatarContent}
          {expanded && (
            <span style={{ fontSize: '12.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split('@')[0] || 'Account'}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
